import {
  buildExcelColumns,
  setExportExcelDefaultColWidths,
  setExportExcelDefaultStyle,
} from '@/pages/dashboard/utils/utils';
import { FILE_TYPES_MAP } from '@gp/constants/const';
import { downloadFile } from '@intelligence/utils/commonUtils';
import { generateExtendedColumns } from '@sg/pages/StudioManagement/InDevProjects/pages/InDevDownload/utils';
import moment from 'moment';

/**
 * 根据 inDevProDataAnalyticsKey 配置获取字段对应的 level
 * @param columnKey 列的 key（如 spend_to_date_value, dev_cost_mom_percent）
 * @returns level 值（1 | 2 | 3 | undefined）
 */
const getFieldLevel = (columnKey: string): number | undefined => {
  const levelKeyList = generateExtendedColumns();
  // 直接用完整 columnKey 在列配置中匹配 level
  const config = levelKeyList.find((item: any) => item.key === columnKey);
  if (columnKey === 'ytm_dev_cost_value' && config?.level === 3) {
    return 3;
  }
  return config?.level;
};

// 基础信息列（Studio / Project / Data Date / Current Milestone / Launch Time）强制红色背景
const BASE_KEYS = ['studio', 'game_title', 'mergeDate', 'current_milestone_milestone', 'launch_time_date'];

/**
 * 根据 level 获取 Excel 单元格样式颜色
 * level: 1 -> 红色 (#FFCDD2)
 * level: 2 -> 蓝色 (#BBDEFB)
 * level: 3 -> 绿色 (#C8E6C9)
 */
const getLevelColor = (
  level?: number,
  isBaseColumn?: boolean,
): { fgColor?: { rgb: string }; bgColor: { rgb: string } } | undefined => {
  if (isBaseColumn) {
    return {
      // fgColor: { rgb: 'FF0000' },
      bgColor: { rgb: 'ffcac8' }, // 浅红色背景
    };
  }
  switch (level) {
    case 1:
      return {
        // fgColor: { rgb: 'FF0000' },
        bgColor: { rgb: 'ffcac8' }, // 浅红色背景
      };
    case 2:
      return {
        // fgColor: { rgb: '0000FF' },
        bgColor: { rgb: 'BBDEFB' }, // 浅蓝色背景
      };
    case 3:
      return {
        // fgColor: { rgb: '008000' },
        bgColor: { rgb: 'C8E6C9' }, // 浅绿色背景
      };
    default:
      return undefined;
  }
};

/**
 * 导出数据为Excel或CSV格式
 */
export function exportData({
  fileName,
  columns,
  data,
  fileType,
}: {
  fileName: string;
  columns: Array<{ header: string; key: string }>;
  data: Record<string, any>[];
  fileType: string;
}) {
  if (fileType === FILE_TYPES_MAP.CSV) {
    exportToCSV({ fileName, columns, data });
  } else {
    exportToExcel({ fileName, columns, data });
  }
}

/**
 * 导出数据为CSV格式（纯文本格式）
 */
function exportToCSV({
  fileName,
  columns,
  data,
}: {
  fileName: string;
  columns: Array<{ header: string; key: string }>;
  data: Record<string, any>[];
}) {
  const headers = columns?.map((item: { header: string }) => item.header) || [];
  const keys = columns?.map((item: { key: string }) => item.key) || [];

  // 创建CSV内容
  let csvContent = headers.join(',') + '\n';

  data?.forEach((item: Record<string, any>) => {
    const row = keys.map((key: string) => {
      const value = item[key];
      // 处理空值
      if (value === null || value === undefined) {
        return '';
      }
      // 处理包含逗号的值
      if (typeof value === 'string' && value.includes(',')) {
        return `"${value}"`;
      }
      return value;
    });
    csvContent += row.join(',') + '\n';
  });

  // 创建下载链接
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  downloadFile({ url, fileName: `${fileName}.csv` });
}

/**
 * 导出数据为Excel格式（表头根据level设置颜色）
 */
function exportToExcel({
  fileName,
  columns,
  data,
}: {
  fileName: string;
  columns: Array<{ header: string; key: string }>;
  data: Record<string, any>[];
}) {
  const excelColumn = buildExcelColumns(columns?.length ?? 0);
  const headerRow: Record<string, any> = {};
  const headerColorMap: Record<string, { rgb: string }> = {}; // 存储每个列的背景色
  const header: string[] = [];
  const keys: string[] = [];

  columns?.forEach((item: any, index: number) => {
    header.push(item.header);
    keys.push(item.key);

    // 根据 key 获取 level 并设置背景色（其余样式沿用默认）
    const level = item?.level;
    // const level = getFieldLevel(item.key, item);
    const isBaseColumn = BASE_KEYS.includes(item.key);
    const colorStyle = getLevelColor(level, isBaseColumn);

    if (colorStyle?.bgColor) {
      headerColorMap[excelColumn[index]] = colorStyle.bgColor;
    }
  });

  header?.forEach((item: string, index: number) => {
    headerRow[excelColumn[index]] = item;
  });
  const excelData: Record<string, any>[] = [headerRow];

  data?.forEach((item: Record<string, any>) => {
    const itemData: Record<string, any> = {};
    keys?.forEach((key: string, index: number) => {
      const value = item[key];
      // 处理数值类型
      if (typeof value === 'number') {
        itemData[excelColumn[index]] = {
          v: value,
          t: 'n',
          z: value % 1 === 0 ? '#,##0;-#,##0' : '##,##0.00 ;-##,##0.00',
        };
      }
      // 处理百分比字段
      else if (key.includes('_percent') && typeof value === 'number') {
        itemData[excelColumn[index]] = {
          v: value,
          t: 'n',
          z: '##,##0.00%" ;-##,##0.00%',
        };
      }
      // 处理空值
      else if (value === null || value === undefined) {
        itemData[excelColumn[index]] = '-';
      }
      // 其他情况
      else {
        itemData[excelColumn[index]] = value;
      }
    });
    excelData.push(itemData);
  });

  // 导出excel（传入表头背景色）
  exportExcel(fileName, excelData, headerColorMap);
}

/**
 * 导出Excel文件
 * @param {string} fileName 文件名
 * @param {object[]} table 原始数值
 * @param {object} headerColorMap 表头背景色映射（仅追加背景色，其余样式沿用默认）
 */

const exportExcel = async (
  fileName: string,
  table: Record<string, any>[] | undefined,
  headerColorMap: Record<string, { rgb: string }> = {},
  colWidth: any[] = [],
) => {
  fileName = fileName?.replace(/\u0020/g, '_');

  // 创建book
  const XLSX = await import('xlsx-js-style');
  const wb = XLSX.utils.book_new();

  // 先应用默认样式（边框、字体、对齐），必须在 json_to_sheet 之前执行，
  // 否则字符串/空值单元格会在生成 sheet 后丢失默认边框样式
  setExportExcelDefaultStyle(table as never[]);

  // json转sheet
  const ws = XLSX.utils.json_to_sheet(table || [], { header: [], skipHeader: true });

  // 仅在默认表头样式基础上追加背景色（第一行）：使用 "列名+行号" 格式（如 A1, B1, C1）
  Object.keys(headerColorMap).forEach((colRef: string) => {
    // 列引用转为单元格引用（A -> A1, B -> B1, ...）
    const cellRef = `${colRef}1`;
    if (ws[cellRef]) {
      const existingStyle = ws[cellRef].s || {};
      // 覆盖背景填充色并将字体设为黑色，其余样式（边框、对齐）沿用默认
      ws[cellRef].s = {
        ...existingStyle,
        fill: {
          ...(existingStyle.fill || {}),
          fgColor: headerColorMap[colRef],
        },
        font: {
          ...(existingStyle.font || {}),
          color: { rgb: '000000' },
          bold: false,
          // sz: 10,
        },
      };
    }
  });

  if (colWidth && colWidth.length > 0) {
    ws['!cols'] = colWidth;
  } else {
    setExportExcelDefaultColWidths(ws, table as never[]);
  }
  // 下载时操作平台不允许输入半角冒号，所以用全角的代替
  const timestamp = moment().format('YYYY_MM_DD HH_mm_ss');
  // sheet写入book
  XLSX.utils.book_append_sheet(wb, ws, 'file');
  // 输出
  XLSX.writeFile(wb, fileName ? `${fileName}.xlsx` : `${timestamp}.xlsx`);
};
